import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, Button, Chip, Collapse, CircularProgress } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { sampleContent } from '../../assets/sampleContent/sampleContent';
import { slugify } from '../../utils/slugify';
import { punchline, stampClass, verdictKey } from '../../utils/myths';

// Keyword patterns mapped to category tags used in sampleContent.
// Order matters -- earlier entries win ties when we score matches.
const CATEGORY_KEYWORDS = [
  { category: 'EV fires',      keywords: ['fire', 'burn', 'explod', 'flamma', 'catch fire'] },
  { category: 'Cost',          keywords: ['expensive', 'cost', 'afford', 'price', 'cheap', 'money', 'overprice'] },
  { category: 'Battery life',  keywords: ['battery', 'batteries', 'dies', 'degrade', 'replace the battery', 'battery last'] },
  { category: 'Emissions',     keywords: ['emission', 'pollute', 'coal', 'dirty', 'environment', 'carbon', 'co2', 'worse for the environment', 'mining'] },
  { category: 'Range',         keywords: ['range', 'mile', 'trip', 'run out', 'too far', 'stranded'] },
  { category: 'Charging',      keywords: ['charge', 'charging', 'charger', 'plug', 'wait in line', 'station'] },
  { category: 'Subsidies',     keywords: ['subsid', 'tax credit', 'handout', 'government', 'rebate'] },
  { category: 'Child labor',   keywords: ['child labor', 'cobalt', 'congo', 'kids mining'] },
  { category: 'Cold weather',  keywords: ['cold', 'winter', 'freez', 'snow'] },
  { category: 'Tesla',         keywords: ['tesla', 'elon', 'musk'] },
  { category: 'Politics & Policy', keywords: ['biden', 'trump', 'liberal', 'democrat', 'republican', 'mandate'] },
  { category: 'EV sales',      keywords: ['nobody buys', 'not selling', 'sitting on lots', 'ev sales'] },
  { category: 'Reliability',   keywords: ['reliable', 'break down', 'unreliable'] },
  { category: 'Hybrid',        keywords: ['hybrid', 'prius'] },
  { category: 'Safety',        keywords: ['safety', 'safe', 'crash', 'dangerous'] },
  { category: 'Climate',       keywords: ['climate', 'global warming'] },
];

/**
 * Return the best-matching sampleContent item for a given comment by
 * scoring category keyword hits. Returns null if nothing matches.
 */
function findBestMatch(text) {
  if (!text || text.trim().length < 3) return null;
  const lower = text.toLowerCase();

  // Score categories by total keyword hits
  const categoryScores = CATEGORY_KEYWORDS.map(({ category, keywords }) => {
    const hits = keywords.reduce((n, kw) => (lower.includes(kw) ? n + 1 : n), 0);
    return { category, hits };
  }).filter(c => c.hits > 0);

  if (categoryScores.length === 0) return null;
  categoryScores.sort((a, b) => b.hits - a.hits);
  const topCategories = categoryScores.slice(0, 2).map(c => c.category);

  // Find first content item that matches the top-scoring category
  const match = sampleContent.find(item =>
    item.categories?.some(cat => topCategories.includes(cat))
  );
  return match || null;
}

/**
 * Return up to `n` sampleContent items relevant to the comment, best first.
 * Used to ground the AI rebuttal generator in vetted, sourced facts.
 */
function findTopMatches(text, n = 3) {
  if (!text || text.trim().length < 3) return [];
  const lower = text.toLowerCase();
  const categoryScores = CATEGORY_KEYWORDS.map(({ category, keywords }) => {
    const hits = keywords.reduce((acc, kw) => (lower.includes(kw) ? acc + 1 : acc), 0);
    return { category, hits };
  }).filter(c => c.hits > 0).sort((a, b) => b.hits - a.hits);

  const topCategories = categoryScores.slice(0, 3).map(c => c.category);
  if (topCategories.length === 0) return [];

  const seen = new Set();
  const out = [];
  for (const cat of topCategories) {
    for (const item of sampleContent) {
      if (item.categories?.includes(cat) && !seen.has(item.id)) {
        seen.add(item.id);
        out.push(item);
        if (out.length >= n) return out;
      }
    }
  }
  return out;
}

export default function RoastComment({ defaultCollapsed = false }) {
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(!defaultCollapsed);
  const [loading, setLoading] = useState(false);
  const [aiRebuttal, setAiRebuttal] = useState(null); // AI-generated text, when available

  const match = useMemo(() => (submitted ? findBestMatch(input) : null), [submitted, input]);

  const handleRoast = async () => {
    setSubmitted(true);
    setCopied(false);
    setAiRebuttal(null);

    const topMatches = findTopMatches(input, 3);
    if (window.gtag) {
      window.gtag('event', 'roast_comment', {
        matched: topMatches.length > 0,
        length: input.length,
      });
    }

    // Try the AI rebuttal generator (dormant without a server key, then we
    // just fall back to the static matched card below).
    if (topMatches.length > 0) {
      setLoading(true);
      try {
        const res = await fetch('/api/roast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            comment: input.slice(0, 1000),
            facts: topMatches.map(m => ({
              title: m.title,
              truth: m.truth,
              truthSnippet: m.truthSnippet,
              sources: m.sources,
            })),
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.matched && data.rebuttal) {
            setAiRebuttal(data.rebuttal);
            if (window.gtag) window.gtag('event', 'roast_ai_success', {});
          }
        }
      } catch (e) {
        /* fall back to static match silently */
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReset = () => {
    setInput('');
    setSubmitted(false);
    setCopied(false);
    setAiRebuttal(null);
    setLoading(false);
  };

  const handleCopy = () => {
    if (!match && !aiRebuttal) return;
    const slug = (match || {}).title ? slugify(match.title) : null;
    const shareUrl = slug ? `${window.location.origin}/s/${slug}.html` : window.location.origin;
    // Prefer the AI-generated rebuttal when we have one; otherwise the verdict
    // and the card's one-line answer.
    const bodyText = aiRebuttal || (match ? `"${match.title}": ${match.titleBadge}. ${punchline(match)}` : '');
    const text = `${bodyText}\n\nMore: ${shareUrl}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      if (window.gtag) {
        window.gtag('event', 'roast_copy_response', { title: match?.title, ai: !!aiRebuttal });
      }
    });
  };

  const scrollToCard = () => {
    if (!match) return;
    if (window.gtag) window.gtag('event', 'roast_see_sources', { title: match.title });
    const slug = slugify(match.title);
    if (document.querySelector('[data-fi-desk]')) {
      // The homepage desk is on this page: it pops the myth (even one past its
      // "Show all" preview) and scrolls to it.
      window.dispatchEvent(new CustomEvent('fi:pop', { detail: slug }));
    } else {
      // The card isn't rendered here -- e.g. Roast embedded on a landing/funnel
      // page that has no myth feed. Send the reader to the card's canonical spot
      // on the homepage; the ?s= deep link auto-expands it and shows its sources
      // (see the matchers in Main.jsx and IdioticStatementCard.jsx). Guards the
      // button against silently doing nothing wherever this widget is dropped.
      window.location.href = `/?s=${slug}`;
    }
  };

  const handleToggle = () => {
    setOpen(prev => {
      const next = !prev;
      if (next && !prev && window.gtag) {
        window.gtag('event', 'roast_open', {});
      }
      return next;
    });
  };

  return (
    <Box sx={{
      backgroundColor: '#fff',
      borderRadius: 0,
      padding: { xs: open ? '20px' : '14px 16px', md: open ? '28px' : '16px 20px' },
      border: '2px solid #111111',
      boxShadow: open ? '6px 6px 0 #111' : '4px 4px 0 #111',
      mb: 4,
      transition: 'padding 0.2s ease, box-shadow 0.2s ease',
    }}>
      <Box
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
        aria-expanded={open}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <AutoAwesomeIcon sx={{ color: '#D7140F' }} />
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#111111' }}>
          Roast-a-Comment
        </Typography>
        {!open && (
          <Typography sx={{
            fontSize: { xs: '13px', md: '14px' },
            color: '#333333',
            ml: 0.5,
            display: { xs: 'none', sm: 'inline' },
          }}>
            &middot; paste a dumb EV take, get the receipts
          </Typography>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <ExpandMoreIcon sx={{
            color: '#111111',
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }} />
        </Box>
      </Box>

      <Collapse in={open} timeout={220} unmountOnExit>
      <Typography sx={{ fontSize: '14px', color: '#333333', mt: 1, mb: 2 }}>
        Paste any dumb thing someone said about EVs. We'll find the receipts.
      </Typography>

      <TextField
        value={input}
        onChange={(e) => { setInput(e.target.value); if (submitted) setSubmitted(false); }}
        placeholder="e.g. 'EVs are terrible for the environment because of all the coal that charges them'"
        multiline
        minRows={2}
        maxRows={6}
        fullWidth
        inputProps={{ maxLength: 500 }}
        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0, backgroundColor: '#F4F4F4' } }}
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          onClick={handleRoast}
          disabled={!input.trim() || loading}
          sx={{
            backgroundColor: '#FFE600',
            color: '#111111',
            border: '2px solid #111111',
            boxShadow: '4px 4px 0 #111',
            borderRadius: 0,
            padding: '8px 22px',
            fontWeight: 800,
            textTransform: 'none',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': { backgroundColor: '#FFE600', transform: 'translate(-2px, -2px)', boxShadow: '6px 6px 0 #111' },
            '&:active': { transform: 'translate(2px, 2px)', boxShadow: '2px 2px 0 #111' },
            // Same button, just faded: reads as "type something first", not broken.
            '&.Mui-disabled': {
              backgroundColor: '#FFE600',
              color: '#111111',
              opacity: 0.55,
              pointerEvents: 'auto',
              cursor: 'not-allowed',
              transform: 'none',
              boxShadow: '4px 4px 0 #111',
            },
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#111111' }} /> : 'Roast it'}
        </Button>
        {submitted && (
          <Button onClick={handleReset} sx={{ color: '#111111', textTransform: 'none', fontWeight: 600, textDecoration: 'underline' }}>
            Try another
          </Button>
        )}
      </Box>

      {/* Result */}
      {submitted && loading && (
        <Box sx={{ mt: 2.5, p: 2, display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#F4F4F4', borderRadius: 0, border: '2px solid #111111' }}>
          <CircularProgress size={18} sx={{ color: '#111111' }} />
          <Typography sx={{ fontSize: '14px', color: '#111111', fontWeight: 600 }}>
            Digging up the receipts&hellip;
          </Typography>
        </Box>
      )}

      {submitted && !loading && !match && (
        <Box sx={{ mt: 2.5, p: 2, backgroundColor: '#FFF9C4', borderRadius: 0, border: '2px solid #111111' }}>
          <Typography sx={{ fontSize: '14px', color: '#111111' }}>
            Hmm, we couldn't auto-match that one. Try the fact-check desk above: there's a good chance the answer is in there.
          </Typography>
        </Box>
      )}

      {submitted && !loading && match && (
        <Box sx={{ mt: 2.5, p: { xs: 2, md: 2.5 }, backgroundColor: '#F4F4F4', borderRadius: 0, border: '2px solid #111111' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Chip label={aiRebuttal ? 'Your comeback' : 'Receipts'} size="small" sx={{ backgroundColor: '#111111', color: '#fff', fontWeight: 700, fontSize: '12px' }} />
            {match.categories?.slice(0, 2).map(cat => (
              <Chip key={cat} label={cat} size="small" sx={{ backgroundColor: '#FFE600', color: '#111111', border: '1px solid #111111', fontWeight: 700, fontSize: '12px' }} />
            ))}
          </Box>
          {/* Only a false claim gets struck through: 9 of the 34 are true or misleading. */}
          <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#111111', mb: 0.75, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <span className={stampClass(match)}>{match.titleBadge}</span>
            <span style={verdictKey(match) === 'false' ? { textDecoration: 'line-through', textDecorationColor: '#D7140F' } : undefined}>{match.title}</span>
          </Typography>
          {aiRebuttal ? (
            <Typography sx={{ fontSize: '15px', color: '#111111', lineHeight: 1.65, mb: 1.5 }}>
              {aiRebuttal}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '14px', color: '#111111', lineHeight: 1.6, mb: 1.5 }}>
              {punchline(match)}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={handleCopy}
              sx={{
                backgroundColor: '#111111',
                color: '#fff',
                borderRadius: 0,
                padding: '6px 16px',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: '#333333' },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
              {copied ? 'Copied!' : 'Copy the response'}
            </Button>
            <Button
              onClick={scrollToCard}
              sx={{
                border: '1px solid #111111',
                color: '#111111',
                borderRadius: 0,
                padding: '6px 16px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: '#FFF6B8' },
              }}
            >
              See sources &rarr;
            </Button>
          </Box>
        </Box>
      )}
      </Collapse>
    </Box>
  );
}
